import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "lucide-react";

type AvatarProps = {
  userId: string;
  avatarId: number;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  useLogoForSystem?: boolean;
};

export function Avatar({ 
  userId, 
  avatarId, 
  size = "md", 
  className,
  useLogoForSystem = false 
}: AvatarProps) {
  const isSystemAccount = userId === "system" || useLogoForSystem;
  
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-10 h-10",
    lg: "w-12 h-12",
    xl: "w-16 h-16",
  };

  const iconSizes = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
    xl: "w-8 h-8",
  };

  useEffect(() => {
    setImgLoaded(false);
    setHasError(false);
    
    const fetchAvatarUrl = async () => {
      if (isSystemAccount) {
        setAvatarUrl("/lovable-uploads/696668bf-afe7-4791-9555-262f2b681308.png");
        return;
      }
      
      // Use a simple numbered avatar approach - generate a consistent color/initial
      // For now, just set null to show the fallback icon
      try {
        const { data: storageData } = await supabase
          .storage
          .from('avatars')
          .createSignedUrl(`avatar-${avatarId}.png`, 60 * 60);
        
        if (storageData?.signedUrl) {
          setAvatarUrl(storageData.signedUrl);
        } else {
          setHasError(true);
        }
      } catch (error) {
        console.error("Error fetching avatar:", error);
        setHasError(true);
      }
    };
    
    fetchAvatarUrl();
  }, [avatarId, isSystemAccount]);

  // Show fallback icon if no avatar or error
  if (hasError || (!avatarUrl && !isSystemAccount)) {
    return (
      <div 
        className={cn(
          "relative rounded-full overflow-hidden bg-brand-cyan/20 flex items-center justify-center",
          sizeClasses[size],
          className
        )}
      >
        <User className={cn("text-brand-cyan", iconSizes[size])} />
      </div>
    );
  }

  return (
    <div 
      className={cn(
        "relative rounded-full overflow-hidden bg-secondary",
        sizeClasses[size],
        className
      )}
    >
      {!imgLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted">
          <div className="animate-pulse bg-muted-foreground/20 rounded-full w-full h-full"></div>
        </div>
      )}
      {avatarUrl && (
        <img
          src={avatarUrl}
          alt="User avatar"
          className={cn(
            "w-full h-full object-cover transition-opacity duration-300",
            imgLoaded ? "opacity-100" : "opacity-0"
          )}
          onLoad={() => setImgLoaded(true)}
          onError={() => setHasError(true)}
        />
      )}
    </div>
  );
}
